import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, Eye, Warning } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { promptSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-click repair for corrupted tournament ELO data.
 *
 * Context (Apr 2026, K Cup 2026): several players played tournament matches
 * with wrong starting ELOs — challengers seeded at 2500, some premiers at
 * 1000. Every per-match ELO delta since was computed from the wrong base,
 * leaving rankings impossible to interpret.
 *
 * This card applies the agreed baselines and tells the backend to:
 *   1. Set each listed player's `baseline_elo` + `tier`
 *   2. Reset their `elo_rating` to the baseline
 *   3. Clear stale ELO history + per-match ELO fields
 *   4. Replay every completed match in the in-progress tournament(s)
 *      chronologically — the deltas recomputed cleanly from the right base.
 *
 * Runs in dry-run first so the admin sees exactly what will change.
 */
/**
 * Tier ladder ordering & baselines.
 *
 * Read at runtime from `/api/admin/tier-ladder` (single source of truth, edited
 * via the Tier Ladder Settings card on the Admin → Settings tab). Falls back
 * to these defaults if the API call fails.
 */
const DEFAULT_RUNGS = [
  { from_rank: 1, to_rank: 2, baseline_elo: 2500, tier: "premier" },
  { from_rank: 3, to_rank: 4, baseline_elo: 2300, tier: "premier" },
  { from_rank: 5, to_rank: 6, baseline_elo: 2000, tier: "premier" },
  { from_rank: 7, to_rank: 8, baseline_elo: 1700, tier: "premier" },
  { from_rank: 9, to_rank: 10, baseline_elo: 1400, tier: "premier" },
];
const DEFAULT_FALLBACK = { baseline_elo: 1000, tier: "challenger" };

/**
 * One-click repair for corrupted tournament ELO data.
 *
 * Reads the tier ladder from `/api/admin/tier-ladder` (editable in the
 * Tier Ladder Settings card) and assigns baselines based on each player's
 * current `ranking` field. So if rank 1-2 is set to 2500 in the ladder,
 * the two top-ranked players get baseline_elo=2500.
 *
 * After Apply: every match in the ongoing tournament is replayed
 * chronologically through the new baselines.
 */
export default function RepairCorruptedEloCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [ladder, setLadder] = useState(null);

  // Fetch the active ladder once on mount + whenever Settings → Tier Ladder
  // saves (we re-fetch on each Preview/Apply call below to stay fresh).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/tier-ladder`, { withCredentials: true })
      .then((r) => { if (!cancelled) setLadder(r.data); })
      .catch(() => { /* admin auth → silently ignore */ });
    return () => { cancelled = true; };
  }, []);

  const buildPayload = async (dryRun) => {
    // Re-fetch ladder + players so the latest values from Tier Ladder Settings
    // are always honoured.
    const [{ data: latestLadder }, { data: players }] = await Promise.all([
      axios.get(`${API}/admin/tier-ladder`, { withCredentials: true }),
      axios.get(`${API}/players`),
    ]);
    setLadder(latestLadder);
    const fallback = latestLadder?.fallback || { baseline_elo: 1000, tier: "challenger" };
    // Sort internal players by current `ranking` ascending (rank 1 first)
    const internal = players
      .filter((p) => !p.is_external)
      .sort((a, b) => (a.ranking ?? 999) - (b.ranking ?? 999));
    const baselines = internal.map((p, i) => {
      const oneIndexed = i + 1;
      const rung = (latestLadder?.rungs || []).find(
        (r) => r.from_rank <= oneIndexed && oneIndexed <= r.to_rank
      );
      const base = rung?.baseline_elo ?? fallback.baseline_elo;
      const tier = rung?.tier ?? fallback.tier;
      return { player_id: p.id, baseline_elo: base, tier };
    });
    return { baselines, dry_run: dryRun };
  };

  const runPreview = async () => {
    setLoading(true);
    try {
      const body = await buildPayload(true);
      const r = await axios.post(`${API}/admin/repair-corrupted-elos`, body, { withCredentials: true });
      setPreview(r.data);
      const warnings = r.data.warnings || [];
      if (warnings.length > 0) {
        warnings.forEach((w) => toast.warning(w, { duration: 6000 }));
      }
      toast.success(`Preview ready — ${r.data.players?.length} players, ${r.data.matches_to_replay} matches`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      "Apply ELO repair?\n\n" +
      "• Every listed player's baseline + tier will be overwritten\n" +
      "• All tournament match ELO deltas will be recomputed from scratch\n" +
      "• A snapshot is saved — rollback is available under Rebaseline Premier ELO → Rollback latest\n\n" +
      "This is irreversible without the rollback. Continue?"
    )) return;
    const pw = await promptSuperPassword({
      title: "Apply ELO repair?",
      description: "Rebuilds elo_history and player ELO from scratch. A snapshot is saved for rollback.",
      actionLabel: "Apply Repair",
    });
    if (!pw) return;
    setApplying(true);
    try {
      const body = await buildPayload(false);
      const r = await axios.post(`${API}/admin/repair-corrupted-elos`, body, { headers: { "X-Super-Password": pw }, withCredentials: true });
      toast.success(r.data.message);
      setPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card className="bg-card border-destructive/30" data-testid="repair-corrupted-elo-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Wrench className="w-5 h-5 text-destructive" weight="fill" />
          Repair Corrupted Tournament ELO
        </CardTitle>
        <CardDescription className="text-xs mt-1 leading-relaxed">
          Resets every player to their agreed baseline, clears tournament ELO deltas,
          and replays every completed match in the ongoing tournament(s) with the correct
          starting ELO. Use this when you spot impossible numbers like a challenger at 2500+
          or a premier at 1000.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-border bg-background/40 p-3 space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Active tier ladder
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="repair-ladder-summary">
            {(ladder?.rungs || []).map((r, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="truncate">
                  Rank {r.from_rank}{r.from_rank !== r.to_rank ? `–${r.to_rank}` : ""}
                  <span className="text-[10px] text-muted-foreground capitalize ml-1">({r.tier})</span>
                </span>
                <span className="tabular-nums text-primary font-semibold">{r.baseline_elo}</span>
              </div>
            ))}
            <div className="flex items-center justify-between col-span-2 pt-1 mt-1 border-t border-border">
              <span className="text-muted-foreground italic">
                Everyone else <span className="text-[10px] capitalize">({ladder?.fallback?.tier || "challenger"})</span>
              </span>
              <span className="tabular-nums text-muted-foreground">{ladder?.fallback?.baseline_elo ?? 1000}</span>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground italic pt-1">
            Edit values via the Tier Ladder Settings card above ↑
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm" variant="outline" onClick={runPreview} disabled={loading}
            className="border-secondary/40 text-secondary hover:bg-secondary/10"
            data-testid="repair-elo-preview-btn"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            {loading ? "Computing…" : "Preview"}
          </Button>
          <Button
            size="sm" onClick={runApply} disabled={applying || !preview}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="repair-elo-apply-btn"
          >
            <Wrench className="w-4 h-4 mr-1.5" weight="fill" />
            {applying ? "Applying…" : "Apply repair"}
          </Button>
          {!preview && (
            <span className="text-[11px] text-muted-foreground self-center ml-2">
              <Warning className="w-3.5 h-3.5 inline mr-0.5" /> Preview first to enable Apply
            </span>
          )}
        </div>

        {preview && (
          <div className="border border-border rounded-lg" data-testid="repair-elo-preview-results">
            <div className="px-4 py-2 border-b border-border bg-muted/10 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Preview — {preview.players?.length || 0} players
              </span>
              <span className="text-[11px] text-muted-foreground">
                {preview.matches_to_replay} match{preview.matches_to_replay === 1 ? "" : "es"} to replay
                {preview.tournaments?.length > 0 && ` · ${preview.tournaments.map((t) => t.name).join(", ")}`}
              </span>
            </div>
            <div className="divide-y divide-border max-h-80 overflow-y-auto">
              {(preview.players || []).map((p) => {
                const tierChanged = p.tier_before !== p.tier_after;
                const baseChanged = p.baseline_before !== p.baseline_after;
                return (
                  <div key={p.player_id} className="flex items-center gap-3 px-4 py-1.5 text-xs">
                    <Badge
                      variant="outline"
                      className={`text-[10px] capitalize w-20 justify-center ${p.tier_after === "premier" ? "border-primary/30 text-primary" : "border-border"}`}
                    >
                      {p.tier_after}
                      {tierChanged && <span className="ml-1 text-destructive">*</span>}
                    </Badge>
                    <span className="flex-1 font-medium truncate">{p.name}</span>
                    <span className="text-[11px] text-muted-foreground tabular-nums w-20 text-right">
                      elo → {p.elo_after_reset}
                    </span>
                    <span className="tabular-nums w-20 text-right">
                      base {p.baseline_after}
                      {baseChanged && <span className="text-destructive ml-1">*</span>}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground px-4 py-2 border-t border-border">
              Rows marked <span className="text-destructive">*</span> changed tier/baseline.
              After apply, matches will be replayed chronologically so ELOs evolve correctly.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
